import React from "react";
import Highcharts from "highcharts";
import { Info } from "luxon";

import { Box } from "@material-ui/core";

import Heatmap from "components/visuals/Heatmap";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppSelector } from "store";
import { FootfallMeasureType } from "modules/customer/insights/portfolioNew/footfallNew/footfallMeasureType";
import {
    selectAverageDailyFootfallMeasure,
    selectAverageFootfallHeatMapData,
    selectHasErrors,
    selectIsLoading
} from "modules/customer/tools/location/footfallNew/footfallSlice";
import stringUtils from "utils/stringUtils";
import numberFormatter from "utils/numberFormatter";

const getTooltipHeader = (weekday: string, time: number): string => {
    let startTimeString = "";
    let endTimeString = "";

    if (time < 10) {
        startTimeString = "0" + time + ":00";
    }
    else {
        startTimeString = String(time) + ":00";
    }

    if (time === 23) {
        endTimeString = "00:00";
    }
    else {
        if ((time + 1) < 10) {
            endTimeString = "0" + (time + 1) + ":00";
        }
        else {
            endTimeString = String((time + 1)) + ":00";
        }
    }

    return `${weekday} ${startTimeString} - ${endTimeString}`;
};

const AverageDailyFootfall: React.FC = () => {
    const colourPalette = useColourPalette();
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const measure = useAppSelector(selectAverageDailyFootfallMeasure);
    const chartData = useAppSelector(selectAverageFootfallHeatMapData);
    const measureLabel = measure === FootfallMeasureType.NumberOfVisits ? "visits" : "dwell time";

    const series: Highcharts.SeriesOptionsType[] = [];
    series.push({
        type: "heatmap",
        data: chartData.footfallData.map(item => ({
            x: item.hourIndex,
            y: item.dayIndex,
            value: measure === FootfallMeasureType.NumberOfVisits ? item.footfallCount : item.dwellTime,
            custom: item
        })),
        dataLabels: {
            enabled: false
        }
    });

    let weekdays: string[] = Info.weekdays("short", { locale: "en-gb" });
    weekdays = [weekdays.pop() ?? "Sunday"].concat(weekdays);
    const timeRanges: string[] = Array.from({ length: 24 }, (_, i) => i.toString());

    const options: Highcharts.Options = {
        yAxis: {
            categories: weekdays,
            reversed: true,
            title: {
                text: undefined
            }
        },
        xAxis: {
            categories: timeRanges
        },
        colorAxis: {
            minColor: "#27233E",
            maxColor: colourPalette.comparators[0],
            max: measure === FootfallMeasureType.NumberOfVisits ? chartData.maxFootfallCount : chartData.maxDwellTime,
            tickPositions: [0, (measure === FootfallMeasureType.NumberOfVisits ? chartData.maxFootfallCount : chartData.maxDwellTime)],
            labels: {
                formatter: function () {
                    return this.value === 0
                        ? `Low ${measureLabel}`
                        : `High ${measureLabel}`;
                }
            }
        },
        tooltip: {
            headerFormat: ``,
            footerFormat: ``,
            useHTML: true,
            formatter: function () {
                const y = this.y;
                const tooltipHeader = (typeof (y) === "number") ? getTooltipHeader(weekdays[y], this.point.x) : ``;
                return `<table>${stringUtils.tooltipHTML([measure === FootfallMeasureType.NumberOfVisits ? "Visits" : "Dwell time", measure === FootfallMeasureType.NumberOfVisits ? "Dwell time" : "Visits"],
                    {
                        header: tooltipHeader,
                        values: [
                            // @ts-ignore
                            measure === FootfallMeasureType.NumberOfVisits ? numberFormatter.toTruncatedInteger(this.point.custom.footfallCount, 1) : numberFormatter.toDecimalPlaces(this.point.custom.dwellTime, 0) + " minutes",
                            // @ts-ignore
                            measure === FootfallMeasureType.NumberOfVisits ? numberFormatter.toDecimalPlaces(this.point.custom.dwellTime, 0) + " minutes" : numberFormatter.toTruncatedInteger(this.point.custom.footfallCount, 1)
                        ],
                        valueFormatting: `color:${colourPalette.comparators[0]}`
                    }
                )}</table>`;
            }
        },
        series,
        navigation: {
            buttonOptions: {
                verticalAlign: 'bottom',
                y: -40
            }
        }
    };
    return (
        <Box data-cy="footfall-level-time-of-day">
            <Heatmap loading={isLoading} error={hasErrors} options={options} />
        </Box>
    );
};

export default AverageDailyFootfall;
